import {
  EmailStatus,
  getEmailValidationSchema,
} from 'shared/getEmailValidationSchema';
import { TFunction } from 'i18next';
import { Yup } from 'shared/yup-extended';
import { getEmailListValidationSchema } from 'shared/getEmailListValidationSchema';

// regex to validate us postal codes
const postalCodeRegex = /^[0-9]{5}$/;

export type ValidationKey =
  | 'companyName'
  | 'firstName'
  | 'lastName'
  | 'address'
  | 'poNumber'
  | 'email'
  | 'additionalEmails'
  | 'proSeats'
  | 'regularSeats'
  | 'postalCode'
  | 'acceptedTerms';
type SchemaProps = {
  t: TFunction;
  requireExistingUserToLogin: boolean;
  emailValidationCache: Map<string, EmailStatus>;
  fields: ValidationKey[];
};

export const getOrgCommonSignupFormsValidationSchema = (
  props: SchemaProps,
): Yup.AnyObjectSchema => {
  const { t, requireExistingUserToLogin, emailValidationCache, fields } = props;
  const schema = {
    companyName: Yup.string()
      .trim()
      .required(t('organizationPurchase.companyName.required')),
    firstName: Yup.string()
      .trim()
      .required(t('organizationPurchase.firstName.required')),
    lastName: Yup.string()
      .trim()
      .required(t('organizationPurchase.lastName.required')),
    poNumber: Yup.string().trim(),
    address: Yup.string()
      .trim()
      .required(t('organizationPurchase.address.required')),
    email: getEmailValidationSchema({
      cache: emailValidationCache,
      skip: false,
      requiredErrMsg: t('organizationPurchase.email.required'),
      invalidErrMsg: t('organizationPurchase.email.invalid'),
      registeredErrMsg: t('recurlyAccountInformation.registeredEmail'),
      managedErrMsg: t('organization.managedAccount'),
      skipAvailabilityCheck: requireExistingUserToLogin,
    }),
    additionalEmails: getEmailListValidationSchema({
      invalidErrMsg: t('organizationPurchase.email.invalid'),
    }),
    proSeats: Yup.number()
      .typeError(t('organizationPurchase.proSeats.required'))
      .min(0, t('organizationPurchase.proSeats.min'))
      .required(t('organizationPurchase.proSeats.required')),
    regularSeats: Yup.number()
      .typeError(t('organizationPurchase.regularSeats.required'))
      .min(0, t('organizationPurchase.regularSeats.min'))
      .required(t('organizationPurchase.regularSeats.required')),
    postalCode: Yup.string()
      .required(t('organizationPurchase.zipcode.required'))
      .matches(postalCodeRegex, t('organizationPurchase.zipcode.invalid')),
    acceptedTerms: Yup.boolean().isTrue(
      t('organizationPurchase.acceptTerms.required'),
    ),
  };

  // Filter the schema to only include the fields that are in the `fields` array
  const filteredSchema: Record<string, Yup.AnySchema> = Object.fromEntries(
    Object.keys(schema)
      .filter((key) => fields.includes(key as ValidationKey))
      .map((key) => [key, schema[key as ValidationKey]]),
  );

  const yupObject = Yup.object().shape(filteredSchema);

  // if fields include proSeats and regularSeats, add a custom validation to check if the sum is greater than 0
  if (fields.includes('proSeats') && fields.includes('regularSeats')) {
    return yupObject.test(
      'seats-total',
      t('organizationPurchase.seats.total'),
      (value) => {
        const { proSeats, regularSeats } = value as {
          proSeats: number;
          regularSeats: number;
        };
        return proSeats + regularSeats > 0;
      },
    );
  }

  return yupObject;
};
